import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { FormControl } from 'react-bootstrap';
import Empty from '../../components/Empty.jsx';
import {
  loadingContent,
  noResultsContent,
  portfoliosContent,
} from '../../components/EmptyStates.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  selectCurrentOrganizationId,
  selectUser,
  selectUserPermissions,
} from '../../modules/auth.js';
import {
  retrieveOrganization,
  selectOrganization,
  selectRetrieveOrganizationState,
} from '../../modules/organizations.js';
import {
  deleteProject,
  retrieveIsShowAlertOnDelete,
  retrieveProjects,
  selectRetrieveProjectsState,
  selectSearchProjectsResults,
} from '../../modules/projects.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
  selectRetrieveStatusesState,
} from '../../modules/projectStatuses.js';
import ProjectsHeader from '../../components/headers/PageHeaders/Project/ProjectsHeader.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import StatusFilter from '../../components/projects/StatusFilter.jsx';
import ProjectsTable from '../../components/projects/ProjectsTable.jsx';
import {
  deleteParentPortfolio,
  deletePortfolio,
  retrievePortfolios,
  searchPortfolios,
  selectPortfolios,
  selectRetrievePortfoliosState,
  selectSearchNoPortfolioEntity,
  selectSearchParentPortfoliosResult,
  selectSearchPortfoliosFiltered,
  selectSearchPortfolioState,
} from '../../modules/portfolio.js';
import ToggleBlock from '../../components/ToggleBlock.jsx';
import ProjectsActionDropdown from '../../components/projects/ProjectsActionDropdown.jsx';
import ParentPortfoliosList from '../../components/projects/ParentPortfoliosList.jsx';
import PortfoliosList from '../../components/projects/PortfoliosList.jsx';
import { DeleteModal } from '../../components/modals/DeleteModal.jsx';
import { isAuthorized } from '../../components/AuthorizedAction.js';
import MyProjectSelect from '../../components/projects/MyProjectSelect.jsx';
import { selectDuplicationsVersion } from '../../modules/projectDuplications.js';

const mapStateToProps = (state) => {
  return {
    user: selectUser(state),
    projects: selectSearchProjectsResults(state),
    searchResult: selectSearchPortfoliosFiltered(state),
    noPortfolioEntity: selectSearchNoPortfolioEntity(state),
    parentPortfolios: selectSearchParentPortfoliosResult(state),
    portfolios: selectPortfolios(state),
    projectStatuses: selectProjectStatusesWithArchived(state),
    organizationId: selectCurrentOrganizationId(state),
    organization: selectOrganization(state),
    retrieveOrganizationSuccess: selectRetrieveOrganizationState(state).success,
    retrieveProjectsSuccess: selectRetrieveProjectsState(state).success,
    retrievePortfoliosSuccess: selectRetrievePortfoliosState(state).success,
    searchPortfoliosSuccess: selectSearchPortfolioState(state).success,
    retrieveStatusesSuccess: selectRetrieveStatusesState(state).success,
    permissions: selectUserPermissions(state),
    duplicationsVersion: selectDuplicationsVersion(state),
  };
};

const mapDispatchToProps = {
  retrieveProjectStatuses,
  retrieveProjects,
  retrievePortfolios,
  retrieveIsShowAlertOnDelete,
  deleteProject,
  deletePortfolio,
  deleteParentPortfolio,
  searchPortfolios,
  retrieveOrganization,
};

const ProjectsContainer = ({
  user,
  projects,
  searchResult,
  noPortfolioEntity,
  parentPortfolios,
  portfolios,
  archived,
  projectStatuses,
  organization,
  retrieveProjectsSuccess,
  retrieveProjectStatuses,
  retrieveOrganization,
  retrieveStatusesSuccess,
  searchPortfoliosSuccess,
  retrievePortfoliosSuccess,
  retrieveOrganizationSuccess,
  retrieveProjects,
  retrievePortfolios,
  deleteProject,
  deletePortfolio,
  deleteParentPortfolio,
  retrieveIsShowAlertOnDelete,
  searchPortfolios,
  organizationId,
  permissions,
  duplicationsVersion,
}) => {
  const defaultQueryState = {
    search: '',
    portfolioStatusIds: [],
    projectStatusIds: [],
    active: !archived,
    ownerIds: [],
  };

  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const isSearchingRef = useRef(false);
  const [deleteProjectState, setDeleteProjectState] = useState({
    showModal: false,
    projectId: null,
  });
  const [showEmpty, setShowEmpty] = useState(false);

  const [emptyContent, setEmptyContent] = useState({});

  const [queryState, setQueryState] = useState(defaultQueryState);

  const [entities, setEntities] = useState({
    portfolios: [],
    projects: [],
  });

  const [availableStatusIds, setAvailableStatusIds] = useState({
    portfolioStatusIds: [],
    projectStatusIds: [],
  });

  const projectOwners = React.useMemo(() => {
    const ownersMap = new Map();
    projects.forEach((project) => {
      (project.owners || []).forEach((owner) => {
        if (!ownersMap.has(owner.id)) ownersMap.set(owner.id, owner);
      });
    });
    return Array.from(ownersMap.values());
  }, [projects]);

  const refetch = async (defaultQueryState) => {
    setAvailableStatusIds({ portfolioStatusIds: [], projectStatusIds: [] });
    try {
      await Promise.all([
        retrieveProjects(),
        searchPortfolios(defaultQueryState || queryState),
      ]);

      setIsFirstLoading(false);
    } catch (error) {
      console.error('Caught an error:', error);
    }
  };

  const onConfirmDeleteProject = (projectId) => {
    deleteProject(projectId || deleteProjectState.projectId).then(() => {
      setQueryState(defaultQueryState);
      refetch(defaultQueryState);
      setDeleteProjectState({ showModal: false, projectId: null });
    });
  };

  const onDeleteProject = (projectId) => {
    retrieveIsShowAlertOnDelete(projectId).then(({ showAlert }) => {
      setDeleteProjectState({ showModal: showAlert, projectId });
      if (!showAlert) {
        window.confirm(
          `${getOrgTermsContent('deleteProject')} \n\n${getOrgTermsContent('deleteProjectDescription')} `
        ) && onConfirmDeleteProject(projectId);
      }
    });
  };

  const onDeletePortfolio = (portfolioId) => {
    if (!window.confirm(getOrgTermsContent('deletePortfolioDescription')))
      return;
    deletePortfolio(portfolioId).then(() => {
      setQueryState(defaultQueryState);
      refetch(defaultQueryState);
    });
  };

  const onDeleteParentPortfolio = (parentPortfolioId) => {
    if (!window.confirm(getOrgTermsContent('deleteParentPortfolioDescription')))
      return;
    deleteParentPortfolio(parentPortfolioId).then(() => {
      retrieveOrganization(organizationId);
      setQueryState(defaultQueryState);
      refetch(defaultQueryState);
    });
  };

  const handleSearch = (e) =>
    setQueryState({ ...queryState, search: e.target.value });

  const handleSelectStatus = (key, val) => {
    setQueryState((prevState) => {
      if (val) {
        const updatedValues = prevState[key].includes(val)
          ? prevState[key].filter((id) => id !== val)
          : [...prevState[key], val];

        return { ...prevState, [key]: updatedValues };
      }
      return { ...prevState, [key]: [] };
    });
  };
  const handleSelectOwner = (id) => {
    setQueryState((prev) => ({
      ...prev,
      ownerIds: prev.ownerIds.includes(id)
        ? prev.ownerIds.filter((item) => item !== id)
        : [...prev.ownerIds, id],
    }));
  };

  useEffect(() => {
    if (organizationId && user && retrieveOrganizationSuccess) {
      retrieveProjectStatuses({ archived: true });
      retrievePortfolios();
      refetch();

      setEmptyContent({
        ...portfoliosContent,
        title: `Create a Change ${getOrgTermsContent('portfolio')}`,
        subtitle: `Create a change ${getOrgTermsContent('portfolio')} to group related change efforts together to achieve ${getOrgTermsContent('portfolio')}-related goals.`,
        paragraphFirst: `Organizing efforts under a shared ${getOrgTermsContent('portfolio')} gives you a clear view of the status and progress of multiple efforts at once. This visibility helps with decision-making, governance, and tracking change-related impact across the organization.`,
        actionLabel: [
          `Add ${getOrgTermsContent('portfolio')}`,
          `Add ${getOrgTermsContent('project')}`,
        ],
      });
    }
  }, [organizationId, user, retrieveOrganizationSuccess]);

  useEffect(() => {
    if (!isFirstLoading) {
      isSearchingRef.current = true;
      searchPortfolios(queryState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  useEffect(() => {
    isSearchingRef.current = false;
  }, [searchPortfoliosSuccess]);

  // A duplication started or finished: its placeholder appeared, became a project or was removed
  useEffect(() => {
    if (!isFirstLoading) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicationsVersion]);

  useEffect(() => {
    if (
      retrievePortfoliosSuccess &&
      searchPortfoliosSuccess &&
      retrieveProjectsSuccess
    ) {
      const independentProjects = noPortfolioEntity?.projects
        ? noPortfolioEntity.projects.reduce((acc, project) => {
            const foundProject = projects.find((p) => p.id === project.id);
            if (foundProject) {
              acc.push(foundProject);
            }
            return acc;
          }, [])
        : [];

      const portfoliosResult = !archived
        ? searchResult.filter((p) => p.id !== 'no_portfolio')
        : searchResult.filter(
            (p) => p.id !== 'no_portfolio' && p.projects?.length
          );

      setEntities({
        portfolios: portfoliosResult,
        projects: independentProjects,
      });

      const allFilteredProjects = [
        ...independentProjects,
        ...portfoliosResult.flatMap((p) => p.projects || []),
      ];

      setAvailableStatusIds({
        portfolioStatusIds: portfoliosResult.map((p) => p.projectStatusId),
        projectStatusIds: allFilteredProjects.map((p) => p.projectStatusId),
      });

      const isDefaultQuery =
        queryState.search === '' &&
        queryState.portfolioStatusIds.length === 0 &&
        queryState.projectStatusIds.length === 0 &&
        queryState.ownerIds.length === 0;

      // Simple check: if no entities on current tab, show start building
      const hasNoEntities =
        portfoliosResult.length === 0 &&
        independentProjects.length === 0 &&
        (!parentPortfolios ||
          parentPortfolios.filter((pp) => pp.id !== 'no_parent_portfolio')
            .length === 0);

      if (isDefaultQuery && hasNoEntities && !isSearchingRef.current) {
        setShowEmpty(true);
      } else {
        setShowEmpty(false);
      }
    }
  }, [
    retrieveProjectsSuccess,
    retrievePortfoliosSuccess,
    searchPortfoliosSuccess,
    noPortfolioEntity,
    searchResult,
    projects,
    portfolios,
    parentPortfolios,
    archived,
    queryState,
  ]);

  return (
    <React.Fragment>
      <ProjectsHeader />

      <SubHeader>
        <div className="flex" style={{ paddingBottom: '24px' }}>
          <div className="flex" style={{ flexGrow: 1 }}>
            <h2>
              {archived ? 'Archived' : 'Active'}{' '}
              {getOrgTermsContent('projects')}
            </h2>
            <strong className="header-item bordered text-muted">
              Total:&nbsp;
              <span className="text-primary">
                {entities.portfolios.reduce(
                  (sum, item) => sum + item.projects.length,
                  0
                ) + entities.projects.length}
              </span>
            </strong>
          </div>
          <div className="m-l-16" style={{ flexGrow: 1, maxWidth: '200px' }}>
            <FormControl
              placeholder="Search"
              type="text"
              onChange={handleSearch}
              value={queryState.search}
            />
          </div>

          {organization?.canManageCustomSettings ? (
            <div className="m-l-16">
              <ProjectsActionDropdown />
            </div>
          ) : null}
        </div>

        <div className="flex" style={{ flexGrow: 1, gap: '24px' }}>
          <MyProjectSelect
            selectedOwnerIds={queryState.ownerIds}
            onSelectAll={() =>
              setQueryState((prev) => ({ ...prev, ownerIds: [] }))
            }
            onSelectMine={() =>
              setQueryState((prev) => ({ ...prev, ownerIds: [user.id] }))
            }
          />
          <StatusFilter
            subject="portfolio"
            selectedStatusIds={queryState.portfolioStatusIds}
            statuses={projectStatuses.filter((s) =>
              availableStatusIds.portfolioStatusIds.includes(s.id)
            )}
            handleSelect={(val) =>
              handleSelectStatus('portfolioStatusIds', val)
            }
          />
          <StatusFilter
            subject="project"
            selectedStatusIds={queryState.projectStatusIds}
            statuses={projectStatuses.filter((s) =>
              availableStatusIds.projectStatusIds.includes(s.id)
            )}
            handleSelect={(val) => handleSelectStatus('projectStatusIds', val)}
          />
        </div>
      </SubHeader>

      {/* Later reloads (a duplication finished, a project was deleted) keep the list and its expanded sections */}
      {!retrieveOrganizationSuccess ||
      (isFirstLoading && !retrieveProjectsSuccess) ||
      !retrieveStatusesSuccess ? (
        <Empty simplified content={loadingContent} />
      ) : showEmpty ? (
        <Empty
          addBtn
          content={emptyContent}
          actionComponent={
            <div className="flex">
              {isAuthorized('create', 'project', permissions) && (
                <ProjectsActionDropdown
                  position="left"
                  title="Start Building"
                />
              )}
            </div>
          }
        />
      ) : (
        <React.Fragment>
          {!isFirstLoading &&
          !entities.portfolios.length &&
          !entities.projects.length &&
          !parentPortfolios.filter((pp) => pp.id !== 'no_parent_portfolio')
            .length ? (
            <Empty simplified content={noResultsContent} />
          ) : (
            <React.Fragment>
              {organization.hasParentPortfolios ? (
                <ParentPortfoliosList
                  parentPortfolios={parentPortfolios}
                  searchResult={searchResult}
                  projects={projects}
                  projectStatuses={projectStatuses}
                  queryState={queryState}
                  onDeleteProject={onDeleteProject}
                  onDeletePortfolio={onDeletePortfolio}
                  onDeleteParentPortfolio={onDeleteParentPortfolio}
                  isTopLevel={true}
                />
              ) : (
                <PortfoliosList
                  portfolios={entities.portfolios}
                  projects={projects}
                  projectStatuses={projectStatuses}
                  queryState={queryState}
                  onDeleteProject={onDeleteProject}
                  onDeletePortfolio={onDeletePortfolio}
                  isTopLevel={true}
                />
              )}

              {!organization.hasParentPortfolios &&
              entities.projects?.length ? (
                <ToggleBlock
                  titleComponent={
                    <div className="flex">
                      <h3 className="h3">
                        No {getOrgTermsContent('portfolio')}
                      </h3>
                      <div className="flex m-l-16">
                        <h5 className="text-muted m-r-12">
                          {getOrgTermsContent('projects')}:{' '}
                        </h5>
                        <h5>{entities.projects.length || '—'}</h5>
                      </div>
                    </div>
                  }
                >
                  <ProjectsTable
                    projects={entities.projects}
                    onDeleteProject={onDeleteProject}
                    statuses={projectStatuses}
                    onDelete
                  />
                </ToggleBlock>
              ) : null}
            </React.Fragment>
          )}
        </React.Fragment>
      )}

      <DeleteModal
        onDelete={() => onConfirmDeleteProject()}
        show={deleteProjectState.showModal}
        onHide={() =>
          setDeleteProjectState({ showModal: false, projectId: null })
        }
        message={getOrgTermsContent('deleteProjectWithData')}
        title={getOrgTermsContent('deleteProject')}
      />
    </React.Fragment>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectsContainer);
